import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, Globe, CheckCircle } from 'lucide-react';

// Country codes for phone input
const COUNTRY_CODES = [
  { code: '44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '1', country: 'United States', flag: '🇺🇸' },
  { code: '91', country: 'India', flag: '🇮🇳' },
  { code: '971', country: 'UAE', flag: '🇦🇪' },
  { code: '65', country: 'Singapore', flag: '🇸🇬' },
  { code: '61', country: 'Australia', flag: '🇦🇺' },
  { code: '49', country: 'Germany', flag: '🇩🇪' },
  { code: '33', country: 'France', flag: '🇫🇷' },
  { code: '81', country: 'Japan', flag: '🇯🇵' },
  { code: '86', country: 'China', flag: '🇨🇳' },
  { code: '82', country: 'South Korea', flag: '🇰🇷' },
  { code: '31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '34', country: 'Spain', flag: '🇪🇸' },
  { code: '39', country: 'Italy', flag: '🇮🇹' },
  { code: '7', country: 'Russia', flag: '🇷🇺' },
  { code: '55', country: 'Brazil', flag: '🇧🇷' },
  { code: '52', country: 'Mexico', flag: '🇲🇽' },
  { code: '27', country: 'South Africa', flag: '🇿🇦' },
  { code: '234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '254', country: 'Kenya', flag: '🇰🇪' },
];

/**
 * MasterclassApplicationModal - Application form for masterclass
 * 
 * Required fields:
 * - name, email, phone, country_code, trading_level, current_results, open_to_validation
 * 
 * Optional fields:
 * - country, referral_code
 */
const MasterclassApplicationModal = ({ isOpen, onClose, onSubmit, masterclassData }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country_code: '44', // Default to UK
    country: '',
    trading_level: '',
    current_results: '',
    open_to_validation: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [localTime, setLocalTime] = useState('');

  // Default masterclass data (fallback if none provided)
  const defaultMasterclass = {
    title: 'Capital Scaling Masterclass',
    description: "An exclusive session for serious traders ready to scale their capital with discipline and consistency.",
    bullet_points: [
      'Why most traders fail when they try to scale',
      'The validation framework used by professional traders',
      'How to build a capital-ready trading system',
      'Live Q&A with our trading experts'
    ],
    host_name: 'Sinu Sivakumar',
    host_title: 'Founder & CEO',
    date: '2026-02-25',
    date_formatted: 'February 25, 2026',
    time: '14:30',
    time_formatted: '2:30 PM',
    timezone: 'GMT',
    duration_minutes: 45
  };

  // Use provided masterclass data or fallback to default
  const masterclass = masterclassData || defaultMasterclass;

  // Get referral code from URL or localStorage
  const getReferralCode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const refFromUrl = urlParams.get('ref');
    const refFromStorage = localStorage.getItem('referral_code');
    return refFromUrl || refFromStorage || null;
  };
  
  const referralCode = getReferralCode();

  // Calculate local time on mount
  useEffect(() => {
    if (!masterclass.date || !masterclass.time) return;
    
    try {
      const [hours, minutes] = masterclass.time.split(':');
      const webinarDateStr = `${masterclass.date}T${hours}:${minutes}:00`;
      
      let masterclassDate;
      if (masterclass.timezone === 'GMT' || masterclass.timezone === 'UTC') {
        masterclassDate = new Date(webinarDateStr + 'Z');
      } else {
        masterclassDate = new Date(webinarDateStr);
      }
      
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const localTimeString = masterclassDate.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
      setLocalTime(`${localTimeString} (${userTimeZone})`);
    } catch (e) {
      console.error('Error calculating local time:', e);
    }
  }, [masterclass.date, masterclass.time, masterclass.timezone]);

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const validatePhone = (phone) => {
    // Only digits, no + sign
    const regex = /^[0-9]{6,15}$/;
    return regex.test(phone);
  };

  const handleChange = (field, value) => {
    // For phone, strip non-digits
    if (field === 'phone') {
      value = value.replace(/[^0-9]/g, '');
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter digits only (6-15 digits)';
    }
    
    if (!formData.trading_level) {
      newErrors.trading_level = 'Please select your trading level';
    }
    
    if (!formData.current_results) {
      newErrors.current_results = 'Please select your current results';
    }
    
    if (!formData.open_to_validation) {
      newErrors.open_to_validation = 'Please answer this question';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      setShowSuccess(true);
    } catch (error) {
      console.error('Application error:', error);
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Get bullet points
  const bulletPoints = masterclass.bullet_points || masterclass.bulletPoints || [];

  // Success Screen
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="masterclass-modal">
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>
        
        <div className="flex min-h-screen items-center justify-center p-4">
          <div 
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Application Under Review
            </h2>
            
            <p className="text-gray-600 mb-6">
              Thank you for applying! Your application is being reviewed. You'll receive a WhatsApp confirmation shortly with next steps.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                📱 Check your WhatsApp for confirmation and updates about your application status.
              </p>
            </div>
            
            <button
              onClick={onClose}
              className="w-full bg-[#337AF7] text-white py-3 rounded-xl font-semibold hover:bg-[#2563eb] transition-all"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="masterclass-modal">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
            aria-label="Close modal"
            data-testid="masterclass-modal-close"
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
          
          {/* Content */}
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block bg-[#337AF7]/10 text-[#337AF7] px-4 py-1 rounded-full text-sm font-semibold mb-4">
                🎓 Exclusive Masterclass
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                {masterclass.title}
              </h2>
              
              {/* Masterclass Details */}
              <div className="flex flex-wrap items-center justify-center gap-4 text-gray-600 mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#337AF7]" />
                  <span className="font-medium">{masterclass.date_formatted || 'February 25, 2026'}</span>
                </div>
                <div className="h-4 w-px bg-gray-300"></div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#337AF7]" />
                  <span className="font-medium">{masterclass.time_formatted || '2:30 PM'} {masterclass.timezone || 'GMT'}</span>
                </div>
                <div className="h-4 w-px bg-gray-300"></div>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-[#337AF7]" />
                  <span className="font-medium">{masterclass.duration_minutes || 45} Minutes</span>
                </div>
              </div>

              {/* User's Local Time */}
              {localTime && (
                <div className="flex items-center justify-center gap-2 text-sm text-[#337AF7] bg-[#337AF7]/5 px-4 py-2 rounded-lg mb-4">
                  <Globe className="h-4 w-4" />
                  <span>Your local time: <strong>{localTime}</strong></span>
                </div>
              )}
              
              <div className="bg-slate-50 border-l-4 border-[#337AF7] p-4 rounded-lg text-left">
                <p className="text-sm text-gray-700 font-semibold">
                  🎙️ Hosted by: <span className="text-[#337AF7]">{masterclass.host_name || 'Sinu Sivakumar'}</span>
                  {masterclass.host_title && `, ${masterclass.host_title}`}
                </p>
              </div>
            </div>
            
            {/* What You'll Learn */}
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                {masterclass.description}
              </p>
              
              {bulletPoints.length > 0 && (
                <>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    What you'll discover:
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    {bulletPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-green-500 mt-1">✓</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            
            {/* Application Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800 font-semibold text-center">
                ⚠️ This is an application-based masterclass. Limited seats available.
              </p>
            </div>

            {/* Referral Badge */}
            {referralCode && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-green-700 text-center">
                  🎁 Referred by: <strong>{referralCode}</strong>
                </p>
              </div>
            )}
            
            {/* Application Form */}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="masterclass-form">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#337AF7] bg-white text-gray-900 placeholder-gray-400 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John Doe"
                  data-testid="masterclass-form-name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#337AF7] bg-white text-gray-900 placeholder-gray-400 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                  data-testid="masterclass-form-email"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>
              
              {/* Phone with Country Code */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.country_code}
                    onChange={(e) => handleChange('country_code', e.target.value)}
                    className="w-32 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#337AF7] bg-white text-gray-900"
                    data-testid="masterclass-form-country-code"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} +{c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#337AF7] bg-white text-gray-900 placeholder-gray-400 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="7123456789"
                    data-testid="masterclass-form-phone"
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Enter digits only, no spaces or special characters</p>
              </div>
              
              {/* Country (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Country/Region <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#337AF7] bg-white text-gray-900 placeholder-gray-400"
                  placeholder="United Kingdom"
                  data-testid="masterclass-form-country"
                />
              </div>

              {/* Trading Level */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Trading Experience Level <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                    <label 
                      key={level} 
                      className={`flex items-center cursor-pointer px-4 py-2 rounded-lg border transition-all ${
                        formData.trading_level === level 
                          ? 'border-[#337AF7] bg-[#337AF7]/5 text-[#337AF7]' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="trading_level"
                        value={level}
                        checked={formData.trading_level === level}
                        onChange={(e) => handleChange('trading_level', e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{level}</span>
                    </label>
                  ))}
                </div>
                {errors.trading_level && (
                  <p className="text-red-500 text-sm mt-1">{errors.trading_level}</p>
                )}
              </div>

              {/* Current Results */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What are your current trading results? <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {['Profitable', 'Breakeven', 'Losing'].map((result) => (
                    <label 
                      key={result} 
                      className={`flex items-center cursor-pointer px-4 py-2 rounded-lg border transition-all ${
                        formData.current_results === result 
                          ? 'border-[#337AF7] bg-[#337AF7]/5 text-[#337AF7]' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="current_results"
                        value={result}
                        checked={formData.current_results === result}
                        onChange={(e) => handleChange('current_results', e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{result}</span>
                    </label>
                  ))}
                </div>
                {errors.current_results && (
                  <p className="text-red-500 text-sm mt-1">{errors.current_results}</p>
                )}
              </div>

              {/* Open to Validation */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Are you open to validating your trading with structure? <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {['Yes', 'Maybe'].map((option) => (
                    <label 
                      key={option} 
                      className={`flex items-center cursor-pointer px-4 py-2 rounded-lg border transition-all ${
                        formData.open_to_validation === option 
                          ? 'border-[#337AF7] bg-[#337AF7]/5 text-[#337AF7]' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="open_to_validation"
                        value={option}
                        checked={formData.open_to_validation === option}
                        onChange={(e) => handleChange('open_to_validation', e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.open_to_validation && (
                  <p className="text-red-500 text-sm mt-1">{errors.open_to_validation}</p>
                )}
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="masterclass-form-submit"
                className="w-full bg-[#337AF7] text-white py-4 rounded-xl font-semibold 
                         hover:bg-[#2563eb] transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting Application...
                  </span>
                ) : (
                  '🎯 Submit My Application'
                )}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-3">
                By applying, you agree to receive masterclass updates via WhatsApp and email.
                We respect your privacy.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterclassApplicationModal;
